// =============================================================================
// app/settings/muted-words.tsx — local muted-words manager (bare-RN port).
//
// MIGRATION (P3 settings): @expo/vector-icons -> components/ui/Icon; the inline
// expo-router <Stack.Screen options> header is dropped (SettingsStack owns the
// title). The muted-words list is local (services/mutedWords) — unchanged.
//
// APPLE-STANDARD PASS: flat colors.bg replaced with a full-bleed
// <ParticleBackground/> aurora (absoluteFill, under the SettingsStack header).
// The add-row + the flat word rows are consolidated into grouped GlassCards —
// the composer sits in its own glass card, and the muted words become one
// hairline-divided GlassCard (matches blocked.tsx / account.tsx). A section
// caption + count pill give the list context; typography is tightened per skill
// §15 (weight+size hierarchy, negative tracking on titles, body near 0). The
// add button is a scoped GlassButton and removal responds on press (§1). All
// data/nav/a11y and the local mutedWords service are untouched.
// =============================================================================
import { useEffect, useState, useCallback } from 'react';
import {
  View,
  Text,
  TextInput,
  StyleSheet,
  ScrollView,
  TouchableOpacity,
  ActivityIndicator,
  KeyboardAvoidingView,
  Platform,
  useWindowDimensions,
} from 'react-native';
import { Ionicons } from '../../components/ui/Icon';
import { colors, spacing, radius, fonts, typographyScale, getLayout } from '../../constants/theme';
import { GlassCard, glassTokens } from '../../components/glass';
import ParticleBackground from '../../components/ui/ParticleBackground';
import { getMutedWords, addMutedWord, removeMutedWord } from '../../services/mutedWords';

// Lets the user manage the local muted-words list. Posts and comments containing
// any of these are hidden from their feed and threads (see services/mutedWords).
export default function MutedWordsScreen() {
  const { width } = useWindowDimensions();
  const layout = getLayout(width);
  const containerStyle = layout !== 'phone'
    ? { maxWidth: layout === 'desktop' ? 720 : 620, alignSelf: 'center' as const, width: '100%' as const }
    : undefined;

  const [words, setWords] = useState<string[]>([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setWords(await getMutedWords());
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  async function handleAdd() {
    const w = input.trim();
    if (!w) return;
    setInput('');
    setWords(await addMutedWord(w));
  }

  async function handleRemove(word: string) {
    setWords(await removeMutedWord(word));
  }

  const canAdd = !!input.trim();
  const count = words.length;

  return (
    <View style={styles.screen}>
      <ParticleBackground theme="light" />
      <KeyboardAvoidingView
        style={{ flex: 1 }}
        behavior={Platform.OS === 'ios' ? 'padding' : undefined}
      >
        <ScrollView
          contentContainerStyle={[styles.content, containerStyle]}
          keyboardShouldPersistTaps="handled"
          showsVerticalScrollIndicator={false}
        >
          <Text style={styles.intro}>
            Posts and comments containing these words or phrases will be hidden from your
            feed and threads. This list stays private on your device.
          </Text>

          {/* Add row — glass composer */}
          <GlassCard padding={0} style={styles.composer}>
            <View style={styles.addRow}>
              <TextInput
                style={styles.input}
                value={input}
                onChangeText={setInput}
                placeholder="Add a word or phrase"
                placeholderTextColor={colors.faint}
                autoCapitalize="none"
                autoCorrect={false}
                returnKeyType="done"
                onSubmitEditing={handleAdd}
              />
              <TouchableOpacity
                style={[styles.addBtn, !canAdd && styles.addBtnDisabled]}
                onPress={handleAdd}
                disabled={!canAdd}
                activeOpacity={0.85}
                accessibilityRole="button"
                accessibilityLabel="Add muted word"
              >
                <Ionicons name="add" size={22} color={colors.white} />
              </TouchableOpacity>
            </View>
          </GlassCard>

          {/* List */}
          {loading ? (
            <ActivityIndicator color={colors.primary} style={{ marginTop: spacing.xxl }} />
          ) : count === 0 ? (
            <View style={styles.empty}>
              <View style={styles.emptyIcon}>
                <Ionicons name="volume-mute-outline" size={30} color={colors.primary} />
              </View>
              <Text style={styles.emptyText}>No muted words yet</Text>
              <Text style={styles.emptySub}>Add words above to filter them out of your experience.</Text>
            </View>
          ) : (
            <>
              <View style={styles.listHeader}>
                <Text style={styles.sectionTitle}>Muted</Text>
                <View style={styles.countPill}>
                  <Text style={styles.countText}>{count}</Text>
                </View>
              </View>
              <GlassCard padding={0} style={styles.list}>
                {words.map((w, i) => (
                  <View
                    key={w}
                    style={[styles.wordRow, i < count - 1 && styles.wordRowDivider]}
                  >
                    <Text style={styles.wordText} numberOfLines={1}>{w}</Text>
                    <TouchableOpacity
                      onPress={() => handleRemove(w)}
                      hitSlop={{ top: 12, bottom: 12, left: 12, right: 12 }}
                      activeOpacity={0.6}
                      accessibilityRole="button"
                      accessibilityLabel={`Remove ${w}`}
                    >
                      <Ionicons name="close-circle" size={20} color={colors.muted} />
                    </TouchableOpacity>
                  </View>
                ))}
              </GlassCard>
            </>
          )}
        </ScrollView>
      </KeyboardAvoidingView>
    </View>
  );
}

const styles = StyleSheet.create({
  screen: { flex: 1, backgroundColor: colors.bg },
  content: { padding: spacing.lg, paddingBottom: spacing.xxxl },
  intro: {
    ...typographyScale.body,
    fontSize: 14,
    lineHeight: 20,
    color: colors.muted,
    marginBottom: spacing.lg,
    ...fonts.regular,
  },
  composer: { marginBottom: spacing.lg },
  addRow: { flexDirection: 'row', alignItems: 'center', gap: spacing.sm, padding: spacing.sm },
  input: {
    flex: 1,
    fontSize: 16,
    color: colors.ink,
    paddingHorizontal: spacing.sm + 2,
    paddingVertical: spacing.sm + 2,
    ...fonts.regular,
  },
  addBtn: {
    width: 44,
    height: 44,
    borderRadius: radius.md,
    backgroundColor: colors.primary,
    alignItems: 'center',
    justifyContent: 'center',
  },
  addBtnDisabled: { opacity: 0.4 },
  listHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.sm,
    marginBottom: spacing.sm,
    marginLeft: spacing.xs,
  },
  sectionTitle: {
    ...typographyScale.caption,
    color: colors.muted,
    textTransform: 'uppercase',
    letterSpacing: 0.6,
    ...fonts.bold,
  },
  countPill: {
    minWidth: 22,
    height: 20,
    paddingHorizontal: 7,
    borderRadius: 10,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: colors.primary + '18',
  },
  countText: {
    fontSize: 11,
    color: colors.primary,
    ...fonts.bold,
  },
  list: {},
  wordRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: spacing.md,
    paddingVertical: spacing.md,
  },
  wordRowDivider: {
    borderBottomWidth: StyleSheet.hairlineWidth,
    borderBottomColor: glassTokens.border,
  },
  wordText: { flex: 1, fontSize: 15, letterSpacing: -0.1, color: colors.ink, ...fonts.medium },
  empty: { alignItems: 'center', paddingTop: 64, paddingHorizontal: spacing.xl },
  emptyIcon: {
    width: 64,
    height: 64,
    borderRadius: 32,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: colors.white,
    borderWidth: 1,
    borderColor: glassTokens.border,
    marginBottom: spacing.md,
    shadowColor: 'rgba(108,92,231,0.16)',
    shadowOffset: { width: 0, height: 4 },
    shadowOpacity: 1,
    shadowRadius: 12,
    elevation: 3,
  },
  emptyText: {
    ...typographyScale.h3,
    fontSize: 19,
    letterSpacing: -0.3,
    color: colors.ink,
    ...fonts.bold,
  },
  emptySub: {
    ...typographyScale.body,
    fontSize: 14,
    color: colors.muted,
    textAlign: 'center',
    marginTop: spacing.xs,
    lineHeight: 20,
    ...fonts.regular,
  },
});
